import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Switch,
    FormControlLabel,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { CheckCircle, Close, FilterAltOff, FolderOpen, RateReview, VerifiedUser } from '@mui/icons-material';
import { customerVerificationAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';

const STATUS = {
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    IN_REVIEW: 'IN_REVIEW',
};

const buildDisplayName = (item) => {
    if (!item || typeof item !== 'object') return '';
    const fn = item.firstName ?? item.FirstName ?? item.first_name ?? '';
    const ln = item.lastName ?? item.LastName ?? item.last_name ?? '';
    const combined = [fn, ln].filter(Boolean).join(' ').trim();
    if (combined) return combined;
    return (
        item.displayName ??
        item.DisplayName ??
        item.display_name ??
        item.userName ??
        item.UserName ??
        item.user_name ??
        item.fullName ??
        item.FullName ??
        item.full_name ??
        item.name ??
        item.Name ??
        ''
    );
};

const normalizeDoc = (raw, parentUser = {}) => {
    const email =
        raw.email ??
        raw.userEmail ??
        raw.Email ??
        parentUser.email ??
        parentUser.Email ??
        '—';
    const userId = raw.userId ?? raw.UserId ?? parentUser.userId ?? parentUser.UserId ?? null;
    const row = { ...raw, ...parentUser };
    return {
        documentId: raw.documentId ?? raw.id ?? raw.DocumentId ?? 0,
        email,
        userId,
        name: buildDisplayName(row) || '—',
        phone:
            raw.phone ??
            raw.phoneNumber ??
            raw.PhoneNumber ??
            raw.mobile ??
            raw.Mobile ??
            parentUser.phone ??
            parentUser.PhoneNumber ??
            '—',
        documentType: raw.documentType ?? raw.type ?? raw.DocumentType ?? '—',
        status: raw.status ?? raw.verificationStatus ?? raw.Status ?? STATUS.IN_REVIEW,
        documentUrl: raw.documentUrl ?? raw.url ?? raw.fileUrl ?? raw.DocumentUrl ?? '',
        complianceChecked: Boolean(
            raw.complianceChecked ?? raw.ComplianceChecked ?? parentUser.complianceChecked
        ),
    };
};

/**
 * API may return either:
 * - Array of users with nested `documents` / `Documents`
 * - Flat array of document rows (grouped by userId or email)
 */
const parsePendingPayload = (res) => {
    const payload = res?.data?.payload ?? res?.data ?? [];
    const arr = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload?.data)
                ? payload.data
                : [];
    if (!arr.length) return [];

    const first = arr[0];
    const nestedDocs = first?.documents ?? first?.Documents;
    if (Array.isArray(nestedDocs)) {
        return arr.map((user, idx) => {
            const docs = user.documents || user.Documents || [];
            const userId = user.userId ?? user.UserId ?? null;
            const email = user.email ?? user.userEmail ?? user.Email ?? '—';
            const groupKey =
                userId != null && userId !== ''
                    ? `id:${userId}`
                    : `em:${email}:${idx}`;
            return {
                groupKey,
                userId,
                email,
                name: buildDisplayName(user) || '—',
                phone:
                    user.phone ??
                    user.phoneNumber ??
                    user.PhoneNumber ??
                    user.mobile ??
                    '—',
                totalDocumentCount:
                    Number(user.totalDocumentCount ?? user.TotalDocumentCount ?? docs.length) ||
                    docs.length,
                complianceChecked: Boolean(user.complianceChecked ?? user.ComplianceChecked),
                documents: docs.map((d) => normalizeDoc(d, user)),
            };
        });
    }

    const map = new Map();
    arr.forEach((raw) => {
        const doc = normalizeDoc(raw, {});
        const stableKey =
            doc.userId != null && doc.userId !== ''
                ? `id:${doc.userId}`
                : `em:${doc.email}`;
        if (!map.has(stableKey)) {
            map.set(stableKey, {
                groupKey: stableKey,
                userId: doc.userId,
                email: doc.email,
                name: doc.name || '—',
                phone: doc.phone,
                totalDocumentCount: Number(raw.totalDocumentCount ?? raw.TotalDocumentCount) || null,
                complianceChecked: doc.complianceChecked,
                documents: [],
            });
        }
        const g = map.get(stableKey);
        g.documents.push(doc);
        if ((!g.name || g.name === '—') && doc.name && doc.name !== '—') g.name = doc.name;
        if ((!g.phone || g.phone === '—') && doc.phone && doc.phone !== '—') g.phone = doc.phone;
        if (doc.complianceChecked) g.complianceChecked = true;
    });

    return [...map.values()].map((g) => ({
        ...g,
        totalDocumentCount: g.totalDocumentCount || g.documents.length,
    }));
};

const statusColor = (status) => {
    if (status === STATUS.APPROVED) return 'success';
    if (status === STATUS.REJECTED) return 'error';
    return 'warning';
};

const allDocumentsApproved = (group) =>
    group?.documents?.length > 0 &&
    group.documents.every((d) => d.status === STATUS.APPROVED);

const DocumentsVerify = () => {
    const { t } = useLanguage();
    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));

    const statusLabel = useCallback(
        (status) => (status === STATUS.IN_REVIEW ? t('docsVerify.statusPending') : status),
        [t]
    );

    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userGroups, setUserGroups] = useState([]);
    const [alertMsg, setAlertMsg] = useState({ text: '', type: 'info' });
    const [toastOpen, setToastOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Compliance Check states
    const [complianceDialogOpen, setComplianceDialogOpen] = useState(false);
    const [complianceNotes, setComplianceNotes] = useState('');
    const [tempComplianceChecked, setTempComplianceChecked] = useState(false);
    const [handlingComplianceGroup, setHandlingComplianceGroup] = useState(null);
    const [isComplianceSubmitting, setIsComplianceSubmitting] = useState(false);

    const [userDocsOpen, setUserDocsOpen] = useState(false);
    const [selectedGroupKey, setSelectedGroupKey] = useState(null);

    const [reviewOpen, setReviewOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [reviewStatus, setReviewStatus] = useState(STATUS.IN_REVIEW);
    const [reviewNotes, setReviewNotes] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [reviewFromUserDialog, setReviewFromUserDialog] = useState(false);

    const [isBulkReview, setIsBulkReview] = useState(false);
    const [selectedDocIds, setSelectedDocIds] = useState([]);

    const [imageOpen, setImageOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');

    const notify = (text, type = 'info') => {
        setAlertMsg({ text, type });
        setToastOpen(true);
    };

    const loadPendingGroups = useCallback(async () => {
        const res = await customerVerificationAPI.getPendingDocuments(1, 500);
        return parsePendingPayload(res);
    }, []);

    const fetchPendingDocuments = useCallback(async () => {
        setIsLoading(true);
        try {
            const groups = await loadPendingGroups();
            setUserGroups(groups);
            setAlertMsg({ text: '', type: 'info' });
        } catch (e) {
            console.error(e);
            setUserGroups([]);
            notify(
                e?.response?.data?.errorMessage ||
                e?.response?.data?.message ||
                e.message ||
                t('docsVerify.loadFailed'),
                'error'
            );
        } finally {
            setIsLoading(false);
        }
    }, [loadPendingGroups, t]);

    useEffect(() => {
        fetchPendingDocuments();
    }, [fetchPendingDocuments]);

    const selectedUserGroup = useMemo(
        () => userGroups.find((g) => g.groupKey === selectedGroupKey) || null,
        [userGroups, selectedGroupKey]
    );

    const openUserDocumentsDialog = (group) => {
        setSelectedDocIds([]);
        setSelectedGroupKey(group.groupKey);
        setUserDocsOpen(true);
    };

    const closeUserDocumentsDialog = () => {
        setUserDocsOpen(false);
        setSelectedGroupKey(null);
    };

    const handleSelectDoc = (id) => {
        setSelectedDocIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleToggleAllDocs = () => {
        if (!selectedUserGroup) return;
        const allIds = selectedUserGroup.documents.map((d) => d.documentId);

        if (selectedDocIds.length === allIds.length && allIds.length > 0) {
            setSelectedDocIds([]);
        } else {
            setSelectedDocIds(allIds);
        }
    };

    const openReviewDialog = (doc, fromUserDialog = false) => {
        setSelectedDoc(doc);
        setIsBulkReview(false);
        setReviewStatus(STATUS.IN_REVIEW);
        setReviewNotes('');
        setRejectionReason('');
        setReviewFromUserDialog(fromUserDialog);
        setReviewOpen(true);
    };

    const openBulkReviewDialog = () => {
        if (selectedDocIds.length === 0) return;
        setSelectedDoc(null);
        setIsBulkReview(true);
        setReviewStatus(STATUS.IN_REVIEW);
        setReviewNotes('');
        setRejectionReason('');
        setReviewFromUserDialog(true);
        setReviewOpen(true);
    };

    const closeReviewDialog = () => {
        setReviewOpen(false);
        setSelectedDoc(null);
        setIsBulkReview(false);
        setReviewFromUserDialog(false);
    };

    const handleSubmitReview = async () => {
        if (!isBulkReview && !selectedDoc?.documentId) return;
        if (isBulkReview && selectedDocIds.length === 0) return;

        if (reviewStatus === STATUS.REJECTED && !rejectionReason.trim()) {
            notify(t('docsVerify.errRejectedReason'), 'warning');
            return;
        }

        const openedFromUserDialog = reviewFromUserDialog;
        const groupKeyForAutoCompliance = selectedGroupKey;

        setIsSubmitting(true);
        try {
            if (isBulkReview) {
                const payload = {
                    documentIds: selectedDocIds.map(Number),
                    status: reviewStatus,
                    reviewNotes: reviewStatus === STATUS.APPROVED ? reviewNotes.trim() : '',
                    rejectionReason: reviewStatus === STATUS.REJECTED ? rejectionReason.trim() : '',
                };
                await customerVerificationAPI.reviewDocumentsBulk(payload);
                notify(t('docsVerify.bulkMarkedAs').replace('{status}', statusLabel(reviewStatus)), 'success');
                setSelectedDocIds([]);
            } else {
                const payload = {
                    documentId: Number(selectedDoc.documentId),
                    status: reviewStatus,
                    reviewNotes: reviewStatus === STATUS.APPROVED ? reviewNotes.trim() : '',
                    rejectionReason: reviewStatus === STATUS.REJECTED ? rejectionReason.trim() : '',
                };
                await customerVerificationAPI.reviewDocument(payload);
                notify(t('docsVerify.markedAs').replace('{status}', statusLabel(reviewStatus)), 'success');
            }

            closeReviewDialog();

            const groups = await loadPendingGroups();
            setUserGroups(groups);

            if (openedFromUserDialog && groupKeyForAutoCompliance) {
                const g = groups.find((x) => x.groupKey === groupKeyForAutoCompliance);
                if (g && allDocumentsApproved(g)) {
                    notify(t('docsVerify.allApprovedClose'), 'success');
                    closeUserDocumentsDialog();
                }
            }
        } catch (e) {
            console.error(e);
            notify(
                e?.response?.data?.errorMessage ||
                e?.response?.data?.message ||
                e.message ||
                t('docsVerify.reviewFailed'),
                'error'
            );
        } finally {
            setIsSubmitting(false);
        }
    };



    const handleOpenComplianceCheck = (group) => {
        setHandlingComplianceGroup(group);
        setComplianceNotes('All the documents provided for verification are comply with EU/German rules.');
        setTempComplianceChecked(Boolean(group.complianceChecked));
        setComplianceDialogOpen(true);
    };

    const handleConfirmCompliance = async () => {
        if (!handlingComplianceGroup || !handlingComplianceGroup.userId) {
            notify('Invalid user data for compliance check', 'error');
            return;
        }

        setIsComplianceSubmitting(true);
        try {
            await customerVerificationAPI.checkCompliance({
                userId: Number(handlingComplianceGroup.userId),
                complianceChecked: tempComplianceChecked,
                complianceNotes: complianceNotes || (tempComplianceChecked ? 'Verified by Admin' : 'Unverified by Admin')
            });

            notify(`Compliance status updated to ${tempComplianceChecked ? 'Verified' : 'Not Verified'}`, 'success');
            setComplianceDialogOpen(false);
            // Refresh the list
            const refreshGroups = await loadPendingGroups();
            setUserGroups(refreshGroups);
        } catch (e) {
            console.error(e);
            notify(e?.response?.data?.message || 'Failed to update compliance', 'error');
        } finally {
            setIsComplianceSubmitting(false);
        }
    };

    const handleImageOpen = (url) => {
        if (!url) return;
        setSelectedImageUrl(url);
        setImageOpen(true);
    };

    const handleImageClose = () => {
        setImageOpen(false);
        setSelectedImageUrl('');
    };

    const visibleGroups = useMemo(() => {
        const q = searchText.trim().toLowerCase();
        if (!q) return userGroups || [];
        return (userGroups || []).filter((g) => {
            const haystack = [
                g.name,
                g.email,
                g.phone,
                String(g.userId || ''),
                String(g.totalDocumentCount || ''),
                g.complianceChecked ? 'yes' : 'no',
            ]
                .join(' ')
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [userGroups, searchText]);

    const paginatedGroups = useMemo(() => {
        const start = page * rowsPerPage;
        return visibleGroups.slice(start, start + rowsPerPage);
    }, [visibleGroups, page, rowsPerPage]);

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        setPage(0);
    }, [searchText]);

    const colCount = 6;

    return (
        <Box sx={{ px: { xs: 0, sm: 3 }, py: { xs: 2, sm: 3 } }}>
            <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
                {t('docsVerify.title')}
            </Typography>
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: '#fff',
                    borderRadius: 1,
                    boxShadow: '0 2px 4px rgba(124,58,237,0.05)',
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            size="small"
                            fullWidth
                            label={t('docsVerify.searchLabel')}
                            placeholder={t('docsVerify.searchPh')}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md="auto"
                        sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<FilterAltOff />}
                            onClick={() => setSearchText('')}
                            sx={clearFilterButtonSx}
                        >
                            {t('common.clear')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" my={5}>
                    <CircularProgress sx={{ color: '#0F3D3E' }} />
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        boxShadow: '0 2px 4px rgba(124,58,237,0.05)',
                        borderRadius: 2,
                        '& .MuiTableCell-head': {
                            backgroundColor: '#ebf4f4',
                            color: '#0F3D3E',
                            fontWeight: 700,
                        },
                    }}
                >
                    <Table sx={{ minWidth: 960 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('common.name')}</TableCell>
                                <TableCell>{t('common.email')}</TableCell>
                                <TableCell>{t('docsVerify.colPhone')}</TableCell>
                                <TableCell>{t('docsVerify.colDocCount')}</TableCell>
                                <TableCell>{t('docsVerify.colCompliance')}</TableCell>
                                <TableCell>{t('docsVerify.colAction')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleGroups.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={colCount} align="center" sx={{ py: 4, color: '#9ca3af' }}>
                                        {t('docsVerify.empty')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedGroups.map((group) => {
                                    return (
                                        <TableRow key={group.groupKey} hover>
                                            <TableCell>{group.name || '—'}</TableCell>
                                            <TableCell>{group.email || '—'}</TableCell>
                                            <TableCell>{group.phone || '—'}</TableCell>
                                            <TableCell>{group.totalDocumentCount ?? group.documents.length}</TableCell>
                                            <TableCell>
                                                {group.complianceChecked ? (
                                                    <Chip
                                                        icon={<CheckCircle fontSize="small" />}
                                                        size="small"
                                                        color="success"
                                                        label="True"
                                                        sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                                        onClick={() => handleOpenComplianceCheck(group)}
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<VerifiedUser fontSize="small" />}
                                                        size="small"
                                                        color="secondary"
                                                        label="False"
                                                        sx={{ 
                                                            fontWeight: 'bold', 
                                                            cursor: 'pointer',
                                                            background: 'rgba(139, 92, 246, 0.1)',
                                                            color: '#0a2829',
                                                            '&:hover': { background: 'rgba(139, 92, 246, 0.2)' }
                                                        }}
                                                        onClick={() => handleOpenComplianceCheck(group)}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<FolderOpen />}
                                                    onClick={() => openUserDocumentsDialog(group)}
                                                    sx={{
                                                        background: 'linear-gradient(135deg,#1b5a5c,#0a2829)',
                                                    }}
                                                >
                                                    {t('docsVerify.openDocuments')}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={visibleGroups.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                        labelRowsPerPage={t('common.rowsPerPage')}
                    />
                </TableContainer>
            )}

            {/* User documents */}
            <Dialog
                open={userDocsOpen}
                onClose={closeUserDocumentsDialog}
                fullWidth
                maxWidth="md"
                fullScreen={isMobileView}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    {t('docsVerify.userDocsTitle')}
                    <IconButton
                        aria-label="close"
                        onClick={closeUserDocumentsDialog}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedUserGroup && (
                        <>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('common.name')}
                                    </Typography>
                                    <Typography variant="body1">{selectedUserGroup.name || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('common.email')}
                                    </Typography>
                                    <Typography variant="body1">{selectedUserGroup.email || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('docsVerify.colPhone')}
                                    </Typography>
                                    <Typography variant="body1">{selectedUserGroup.phone || '—'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('docsVerify.colCompliance')}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        color={selectedUserGroup.complianceChecked ? 'success' : 'default'}
                                        label={
                                            selectedUserGroup.complianceChecked
                                                ? t('docsVerify.complianceYes')
                                                : t('docsVerify.complianceNo')
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                {t('docsVerify.documentsList')}
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                size="small"
                                                indeterminate={
                                                    selectedDocIds.length > 0 &&
                                                    selectedDocIds.length < selectedUserGroup.documents.length
                                                }
                                                checked={
                                                    selectedUserGroup.documents.length > 0 &&
                                                    selectedDocIds.length === selectedUserGroup.documents.length
                                                }
                                                onChange={handleToggleAllDocs}
                                            />
                                        </TableCell>
                                        <TableCell>{t('docsVerify.colDocType')}</TableCell>
                                        <TableCell>{t('common.status')}</TableCell>
                                        <TableCell>{t('docsVerify.colDocUrl')}</TableCell>
                                        <TableCell align="right">{t('docsVerify.colAction')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedUserGroup.documents.map((row) => (
                                        <TableRow key={row.documentId}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedDocIds.includes(row.documentId)}
                                                    onChange={() => handleSelectDoc(row.documentId)}
                                                    disabled={row.status === STATUS.APPROVED || row.status === STATUS.REJECTED}
                                                />
                                            </TableCell>
                                            <TableCell>{row.documentType || '—'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    color={statusColor(row.status)}
                                                    label={statusLabel(row.status)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {row.documentUrl ? (
                                                    <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() => handleImageOpen(row.documentUrl)}
                                                        sx={{ textTransform: 'none' }}
                                                    >
                                                        {t('docsVerify.viewDoc')}
                                                    </Button>
                                                ) : (
                                                    '—'
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openReviewDialog(row, true)}
                                                    sx={{ color: '#1b5a5c' }}
                                                    title={t('docsVerify.review')}
                                                    disabled={row.status === STATUS.APPROVED || row.status === STATUS.REJECTED}
                                                >
                                                    <RateReview fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                                <Button
                                    variant="contained"
                                    disabled={selectedDocIds.length === 0}
                                    onClick={openBulkReviewDialog}
                                    sx={{
                                        background: 'linear-gradient(135deg,#1b5a5c,#0a2829)',
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        '&.Mui-disabled': {
                                            background: '#e0e0e0',
                                            color: '#9e9e9e'
                                        }
                                    }}
                                >
                                    {t('docsVerify.reviewSelected')} ({selectedDocIds.length})
                                </Button>

                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeUserDocumentsDialog}>{t('common.close')}</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={reviewOpen} onClose={closeReviewDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pr: 6 }}>
                    {isBulkReview ? t('docsVerify.bulkReviewTitle') : t('docsVerify.reviewTitle')}
                    <IconButton
                        aria-label="close"
                        onClick={closeReviewDialog}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {isBulkReview ? (
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {t('docsVerify.bulkReviewDesc').replace('{n}', String(selectedDocIds.length))}
                        </Typography>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{t('common.name')}:</strong> {selectedDoc?.name || '—'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>{t('common.email')}:</strong> {selectedDoc?.email || '—'}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                <strong>{t('docsVerify.colDocType')}:</strong> {selectedDoc?.documentType || '—'}
                            </Typography>
                        </>
                    )}
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>{t('common.status')}</InputLabel>
                        <Select
                            value={reviewStatus}
                            label={t('common.status')}
                            onChange={(e) => setReviewStatus(e.target.value)}
                        >
                            <MenuItem value={STATUS.IN_REVIEW}>{t('docsVerify.statusInReview')}</MenuItem>
                            <MenuItem value={STATUS.APPROVED}>{t('docsVerify.statusApproved')}</MenuItem>
                            <MenuItem value={STATUS.REJECTED}>{t('docsVerify.statusRejected')}</MenuItem>
                        </Select>
                    </FormControl>

                    {reviewStatus === STATUS.APPROVED && (
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                            label={t('docsVerify.fieldReviewNotes')}
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                        />
                    )}

                    {reviewStatus === STATUS.REJECTED && (
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={3}
                            label={t('docsVerify.fieldRejectionReason')}
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeReviewDialog} disabled={isSubmitting}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitReview}
                        disabled={isSubmitting}
                        sx={{ background: 'linear-gradient(135deg,#1b5a5c,#0a2829)' }}
                    >
                        {isSubmitting ? t('docsVerify.submitting') : t('docsVerify.submitReview')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={imageOpen}
                onClose={handleImageClose}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        position: 'relative',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'visible',
                        margin: 0,
                        width: 'auto',
                        maxWidth: 'none'
                    }
                }}
                BackdropProps={{
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.9)' }
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={handleImageClose}
                    sx={{
                        position: 'fixed',
                        right: 20,
                        top: 20,
                        zIndex: 1500,
                        color: '#fff',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        }
                    }}
                >
                    <Close fontSize="large" />
                </IconButton>
                <DialogContent sx={{ p: 0, height: '100vh', width: '100vw', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
                    {selectedImageUrl ? (
                        selectedImageUrl.toLowerCase().includes('.pdf') ? (
                            <Box sx={{ width: '95vw', height: '95vh', p: 0, overflow: 'hidden', backgroundColor: '#fff', borderRadius: '4px' }}>
                                <iframe
                                    src={selectedImageUrl}
                                    title={t('docsVerify.previewTitle')}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 'none' }}
                                />
                            </Box>
                        ) : (
                            <Box
                                component="img"
                                src={selectedImageUrl}
                                alt="Document Preview"
                                sx={{
                                    maxWidth: '95vw',
                                    maxHeight: '95vh',
                                    objectFit: 'contain'
                                }}
                            />
                        )
                    ) : (
                        <Box sx={{ p: 10, textAlign: 'center' }}>
                            <Typography color="white">{t('docsVerify.noImage')}</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={complianceDialogOpen}
                onClose={() => !isComplianceSubmitting && setComplianceDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {t('docsVerify.markCompliance')}
                    <IconButton onClick={() => setComplianceDialogOpen(false)} disabled={isComplianceSubmitting} size="small">
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                            User: {handlingComplianceGroup?.name}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={tempComplianceChecked}
                                    onChange={(e) => setTempComplianceChecked(e.target.checked)}
                                    color="success"
                                />
                            }
                            label={tempComplianceChecked ? "True" : "False"}
                            sx={{ my: 1 }}
                        />
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={t('common.note')}
                        placeholder="Enter compliance check notes (mandatory)..."
                        value={complianceNotes}
                        onChange={(e) => setComplianceNotes(e.target.value)}
                        variant="outlined"
                        autoFocus
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f9fafb' }}>
                    <Button
                        disabled={isComplianceSubmitting}
                        onClick={() => setComplianceDialogOpen(false)}
                        variant="text"
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={isComplianceSubmitting || !complianceNotes.trim()}
                        onClick={handleConfirmCompliance}
                        sx={{
                            background: tempComplianceChecked
                                ? 'linear-gradient(135deg,#10b981,#059669)'
                                : 'linear-gradient(135deg,#1b5a5c,#0a2829)',
                            px: 3
                        }}
                    >
                        {isComplianceSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : tempComplianceChecked ? (
                            t('common.save')
                        ) : (
                            'Update Status'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setToastOpen(false)} severity={alertMsg.type} sx={{ width: '100%' }}>
                    {alertMsg.text}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DocumentsVerify;
